<template>
  <div id="fusion_chat_details_container" class="w-full h-full flex flex-col overflow-hidden">
    <!-- Header: stacked avatar group + counter, channel name (+ unwired rename pencil), participant count -->
    <div class="flex flex-col items-center gap-2 px-8 pt-6 pb-4 flex-shrink-0">
      <div class="flex items-center gap-1">
        <div class="flex items-center">
          <div v-for="(participant, idx) in avatar_group" :key="`channel_avatar_${participant.id}`"
               class="relative rounded-full"
               :style="idx > 0 ? 'margin-left: -16px; box-shadow: 0 0 0 1px var(--background-layer-01);' : ''">
            <messenger-avatar :name="participant.name" :size="32" :bg-color="participant.color" />
          </div>
        </div>
        <span v-if="avatar_overflow_count > 0" class="text-[13px] font-medium text-text-subtle leading-none">
          +{{ avatar_overflow_count }}
        </span>
      </div>

      <div class="flex items-end justify-center gap-1">
        <p class="text-[28px] font-normal text-text-default leading-[1.28] text-center break-words">{{ channel_name }}</p>
        <!-- Rename pencil — hidden until the rename page exists (v-if="false") -->
        <div v-if="false" class="h-9 px-2 flex items-center justify-center rounded-xl flex-shrink-0">
          <i class="ri-edit-line text-[var(--text-button-ghost)] text-[20px]" />
        </div>
      </div>

      <p class="text-[13px] font-medium text-text-subtle leading-[1.35] tracking-[0.1px]">
        {{ $t('messaging.chatDetails.participantCount', participant_count) }}
      </p>
    </div>

    <!-- Participants section: header + unwired add-user action, then the scrollable roster -->
    <div class="flex-1 min-h-0 flex flex-col px-4">
      <div class="flex items-center justify-between flex-shrink-0">
        <p class="text-sm font-semibold text-text-subtle leading-[1.28] tracking-[0.16px]">{{ $t('messaging.chatDetails.participants') }}</p>
        <!-- Add-user icon — hidden until the add-participants-from-details page exists (v-if="false") -->
        <div v-if="false" class="h-9 px-2 flex items-center justify-center rounded-xl flex-shrink-0">
          <i class="ri-user-add-line text-[var(--text-button-ghost)] text-[20px]" />
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto">
        <div v-for="participant in participant_rows" :key="participant.id"
             class="flex items-center gap-2 pl-2"
             :class="participant.is_self ? '' : (hovered_kebab_participant_id === participant.id ? 'cursor-pointer' : 'cursor-pointer hover:bg-black/5')"
             @click="participant.is_self ? null : openParticipant(participant)">
          <messenger-avatar :name="participant.name" :size="32" :bg-color="participant.color" class="flex-shrink-0" />
          <div class="flex-1 min-w-0 flex items-center justify-between gap-2 py-4 border-b border-border-separator">
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-base font-medium text-text-default leading-6 truncate">{{ participant.label }}</span>
              <span v-if="participant.is_owner"
                    class="h-7 pl-2 pr-3 rounded-2xl inline-flex items-center gap-1 flex-shrink-0 bg-[var(--badge-warning)] text-[var(--text-badge-warning)]">
                <i class="ri-vip-crown-line text-base" />
                <span class="text-sm font-normal leading-[1.2]">{{ $t('messaging.chatDetails.owner') }}</span>
              </span>
            </div>
            <!-- Per-row overflow kebab — only for chat owners / enterprise admins, and never on the
                 owner's row or the current user's own row (neither is removable). @click.stop keeps a
                 kebab/menu click from bubbling to the row's openParticipant handler. -->
            <div v-if="participant.can_remove" class="relative flex-shrink-0 mr-2.5" @click.stop>
              <div class="h-9 px-2 flex items-center justify-center rounded-xl cursor-pointer transition-colors hover:bg-button-ghost-hover active:bg-button-ghost-active"
                   :title="$t('navbar.moreOptions')"
                   @mouseenter="setKebabHovered(participant.id)"
                   @mouseleave="setKebabHovered(null)"
                   @click.stop="toggleParticipantMenu(participant.id)">
                <i class="ri-more-fill ri-125" />
              </div>
              <div v-if="open_menu_participant_id === participant.id"
                   class="absolute right-0 top-full mt-1 bg-surface-1 border border-border-separator rounded-xl shadow-md z-10 w-max py-1">
                <div class="flex items-center gap-2 px-3 py-2 text-sm text-danger cursor-pointer hover:bg-button-ghost-hover rounded-lg mx-1 whitespace-nowrap"
                     @click.stop="removeParticipant(participant)">
                  {{ $t('messaging.chatDetails.removeParticipant') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!$FUSarrayHasEntries(participant_rows)" class="px-2 py-4 text-sm text-text-subtle">
          {{ $t('navbar.noParticipants') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>

<script>
  import { mapStores } from 'pinia';
  import { useMainStore } from '@store/main';
  import MessengerAvatar from '@components/Messaging/MessengerAvatar.vue';

  export default {
    name: 'ChatDetails',
    components: {
      'messenger-avatar': MessengerAvatar,
    },
    beforeUnmount() {
      // Tear down the outside-click listener if the page unmounts while a row menu is still open.
      document.removeEventListener('click', this.closeParticipantMenu);
    },
    data() {
      return {
        // Id of the participant whose row kebab menu is open (null = none). Only one opens at a time.
        open_menu_participant_id: null,
        // Id of the participant whose row kebab is currently hovered (null = none). While set, that
        // row drops its own hover tint so the kebab's hover reads cleanly instead of blending in.
        hovered_kebab_participant_id: null,
      };
    },
    computed: {
      ...mapStores(useMainStore),
      open_contact_guid: {
        get() { return this.mainStore.open_contact_guid; },
        set(value) { this.mainStore.open_contact_guid = value; },
      },
      internal_messaging_state: {
        get() { return this.mainStore.internal_messaging_state; },
        set(value) { this.mainStore.internal_messaging_state = value; },
      },
      contact_origin_view: {
        get() { return this.mainStore.contact_origin_view; },
        set(value) { this.mainStore.contact_origin_view = value; },
      },
      active_thread_nav_meta() {
        return this.mainStore.active_thread_nav_meta;
      },
      active_thread_participants() {
        return this.mainStore.active_thread_participants;
      },
      user_access_info() {
        return this.mainStore.user_access_info;
      },
      current_user_guid() {
        return this.user_access_info?.userId || null;
      },
      channel_name() {
        return this.active_thread_nav_meta?.name || 'Channel';
      },
      owner_guid() {
        return this.active_thread_nav_meta?.createdByUserId || null;
      },
      participants_filtered() {
        // Resolved members come from the join response (id, name, email, avatar). System/bot ids
        // (e.g. the AV AI chat user) are filtered out so only real members are listed. The current
        // user is kept — they appear as the "You" row.
        return (this.active_thread_participants || [])
          .filter(u => u && u.id && u.id.indexOf('system_') < 0);
      },
      participant_count() {
        return this.participants_filtered.length;
      },
      participant_rows() {
        // Ordering: owner first, then the current user ("You"), then everyone else alphabetically
        // (case-insensitive). If the owner is the current user, they collapse to a single row.
        const colors = this.$FUSfusionAvatarColorsHex || [];
        const ownerId = this.owner_guid;
        const meGuid = this.current_user_guid;
        const canManage = this.can_manage_channel;

        const alpha = this.participants_filtered.slice()
          .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));

        const owner = ownerId ? alpha.find(p => p.id === ownerId) : null;
        const me = meGuid ? alpha.find(p => p.id === meGuid) : null;
        const ownerIsMe = Boolean(owner && me && owner.id === me.id);

        const head = [];
        if (owner) head.push(owner);
        if (me && !ownerIsMe) head.push(me);

        const headIds = new Set(head.map(p => p.id));
        const ordered = head.concat(alpha.filter(p => !headIds.has(p.id)));

        return ordered.map((p, idx) => {
          const isOwner = Boolean(ownerId) && p.id === ownerId;
          const isSelf = Boolean(meGuid) && p.id === meGuid;
          return {
            id: p.id,
            name: p.name || 'Unknown',
            label: isSelf ? 'You' : (p.name || 'Unknown'),
            is_owner: isOwner,
            // The current user's own row ("You") is not clickable — only other participants open a profile.
            is_self: isSelf,
            // The per-row Remove kebab shows only to a chat owner / enterprise admin, and never on the
            // owner's row or the current user's own row — neither is removable.
            can_remove: canManage && !isOwner && !isSelf,
            color: colors.length ? colors[idx % colors.length] : '',
          };
        });
      },
      avatar_group() {
        return this.participant_rows.slice(0, 3);
      },
      avatar_overflow_count() {
        return Math.max(0, this.participant_count - this.avatar_group.length);
      },
      is_channel_owner() {
        return Boolean(this.owner_guid) && Boolean(this.current_user_guid) && this.owner_guid === this.current_user_guid;
      },
      is_enterprise_admin() {
        // Best-effort UX gate for the per-row Remove kebab only — the server is authoritative on
        // removal (ThreadsController re-derives admin from caller.IsFusionAdmin + the Reynolds
        // global-admin email rule and 403s otherwise; a rejected remove surfaces as messages_error).
        // isFusionAdmin / isGlobalAdmin here are the server-computed UserAccessInfo flags from
        // FusionAccessController, which uses that same rule on the same caller email, so this matches
        // enforcement in practice; the only divergence vector is a client flag gone stale against a
        // token change, in which case the server still has the final say.
        return Boolean(this.user_access_info?.isFusionAdmin || this.user_access_info?.isGlobalAdmin);
      },
      can_manage_channel() {
        return this.is_channel_owner || this.is_enterprise_admin;
      },
    },
    methods: {
      openParticipant(participant) {
        // The "You" row is not a profile target.
        if(!participant || participant.is_self || !participant.id) return;
        // Leave a breadcrumb so Back from the contact profile returns here (Chat/Thread Details)
        // rather than the Contacts list. EnterpriseContactDetails reads open_contact_guid; the
        // open thread stays selected in MessengerWindow, so the nav meta + participants this page
        // reads remain available when we return.
        this.contact_origin_view = 'ChatDetails';
        this.open_contact_guid = participant.id;
        this.internal_messaging_state = 'OpenContact';
      },
      setKebabHovered(id) {
        // Pass a participant id on mouseenter, null on mouseleave. The row's :class binding reads this
        // to drop its hover tint (bg-black/5) while its kebab is hovered, so the kebab's own hover
        // (matching the navbar kebab) is not masked by the near-identical row tint.
        this.hovered_kebab_participant_id = id;
      },
      toggleParticipantMenu(id) {
        // Toggle the row's single-item menu. Opening it registers a one-shot outside-click closer
        // (mirrors Navbar.toggleMoreOptions); the kebab and menu item carry @click.stop so a click
        // on them never reaches this document listener.
        if (this.open_menu_participant_id === id) {
          this.closeParticipantMenu();
          return;
        }
        this.open_menu_participant_id = id;
        document.addEventListener('click', this.closeParticipantMenu);
      },
      closeParticipantMenu() {
        this.open_menu_participant_id = null;
        document.removeEventListener('click', this.closeParticipantMenu);
      },
      removeParticipant(participant) {
        this.closeParticipantMenu();
        if (!participant || !participant.id || !participant.can_remove) return;
        // Thread mutation + roster refresh live in MessengerWindow (it owns the open thread and its
        // join/roster state), same as Delete/Leave. Emit the intent; MessengerWindow calls the store
        // action and refreshes active_thread_participants.
        window.event_bus.$emit('MESSENGER-REMOVE-PARTICIPANT', { userId: participant.id });
      },
    },
  }
</script>
